import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import ContentCutIcon from '@mui/icons-material/ContentCut';

import type { PhaseSpan, TraitFrame } from '../replay/buildTimeline';
import { loadSplitTimeline } from '../replay/splitTimeline';
import { VideoClock } from './VideoClock';
import * as sessionsApi from '../api/sessions';
import { listAttachmentsForTenantSession } from '../api/sessionAttachments';
import { listProcedures, type Procedure } from '../api/procedures';
import type { TimelineEvent } from './CaptureTimeline';
import { SearchableSelect } from './SearchableSelect';

const NO_PROCEDURE = [{ value: '', label: 'None' }];

/**
 * Guided SESSION-SPLIT UI (Phase 3). Loads the session's NDJSON (same
 * presign the replay uses) plus its video (when one is attached), and
 * lets the operator watch the recording, mark split points on a
 * session-elapsed timeline, and choose which segments to keep. Each kept
 * segment becomes a child session; gaps are dropped and the parent is
 * disabled (restorable).
 *
 * The video is synced to the same session-elapsed time base as the trace
 * and the scrubber (video_time = session_time − alignment_offset), so a
 * boundary the operator marks while watching lands at the right cut.
 */

export interface Segment {
  start: number;
  end: number;
  index: number;
}

/** Partition [0, durationS] at the (sorted) boundaries into segments. */
export function deriveSegments(
  durationS: number,
  boundaries: number[],
): Segment[] {
  const cuts = [0, ...[...boundaries].sort((a, b) => a - b), durationS];
  const segs: Segment[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    segs.push({ start: cuts[i], end: cuts[i + 1], index: i });
  }
  return segs;
}

/** The kept segments, as the `{start_s, end_s, name?}` payload for the
 *  API. A blank / whitespace-only name is omitted. */
export function buildSplitPayload(
  segments: Segment[],
  dropped: Set<number>,
  names: Record<number, string> = {},
): sessionsApi.SplitSegmentInput[] {
  return segments
    .filter((s) => !dropped.has(s.index))
    .map((s) => {
      const name = (names[s.index] ?? '').trim();
      return name
        ? { start_s: s.start, end_s: s.end, name }
        : { start_s: s.start, end_s: s.end };
    });
}

/** A split is valid only once there's ≥1 cut and ≥1 kept segment. */
export function canConfirmSplit(
  boundaries: number[],
  keptCount: number,
): boolean {
  return boundaries.length >= 1 && keptCount >= 1;
}

function fmt(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));

type Stage = 'loading' | 'ready' | 'error' | 'submitting';

/** How often the dialog checks on a running split. */
export const SPLIT_POLL_MS = 2000;

interface VideoInfo {
  url: string;
  /** video_time = session_time − offset. */
  offset: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  event: TimelineEvent;
  /** Called after a successful split so the caller can reload the list. */
  onSplitDone: () => void;
}

export function SplitSessionDialog({
  open,
  onClose,
  tenantId,
  event,
  onSplitDone,
}: Props) {
  const [stage, setStage] = useState<Stage>('loading');
  const [error, setError] = useState<string | null>(null);
  // Whether the dialog is still open — a running split stops being followed
  // (not stopped) once it is closed.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  const [durationS, setDurationS] = useState(0);
  const [loadedBytes, setLoadedBytes] = useState(0);
  const [phases, setPhases] = useState<PhaseSpan[]>([]);
  const [traits, setTraits] = useState<TraitFrame[]>([]);
  const [video, setVideo] = useState<VideoInfo | null>(null);
  const [boundaries, setBoundaries] = useState<number[]>([]);
  const [dropped, setDropped] = useState<Set<number>>(new Set());
  const [names, setNames] = useState<Record<number, string>>({});
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [playhead, setPlayhead] = useState(0);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const getVideo = useCallback(() => videoRef.current, []);

  // Load the recording (duration + phases + trace) and the session's
  // video (best-effort) when the dialog opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStage('loading');
    setLoadedBytes(0);
    setError(null);
    setBoundaries([]);
    setDropped(new Set());
    setNames({});
    setPlayhead(0);
    setVideo(null);
    (async () => {
      // The tenant's procedures populate the per-segment name dropdown.
      // Best-effort — a failure just leaves the dropdown empty.
      try {
        const procs = await listProcedures(tenantId);
        if (!cancelled) setProcedures(procs);
      } catch {
        /* no procedures / not reachable — dropdown stays empty */
      }
      // Video is optional — don't fail the whole dialog if it's absent.
      try {
        const atts = await listAttachmentsForTenantSession(tenantId, event.id);
        const vid = atts.find(
          (a) => a.kind === 'video' && a.uploaded_at && a.presigned_get_url,
        );
        if (!cancelled && vid?.presigned_get_url) {
          setVideo({
            url: vid.presigned_get_url,
            offset: vid.audio_sync_offset_s ?? 0,
          });
        }
      } catch {
        /* no video / not reachable — carry on with the trace only */
      }
      try {
        const { url } = await sessionsApi.getSessionReplayUrl(
          tenantId,
          event.id,
        );
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`download failed (${resp.status})`);
        // Streamed: a long recording is more than the browser can hold as
        // one string (see splitTimeline.ts).
        const tl = await loadSplitTimeline(
          resp,
          (bytes) => setLoadedBytes(bytes),
          () => cancelled,
        );
        if (cancelled || tl === null) return;
        if (tl.durationS <= 0) {
          throw new Error('This recording has no sensor samples to split.');
        }
        setDurationS(tl.durationS);
        setPhases(tl.phases);
        setTraits(tl.traits);
        setStage('ready');
      } catch (e) {
        if (!cancelled) {
          setError(
            (e as Error).message || 'Could not load this recording to split.',
          );
          setStage('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tenantId, event.id]);

  const segments = useMemo(
    () => deriveSegments(durationS, boundaries),
    [durationS, boundaries],
  );
  const keptCount = segments.length - dropped.size;

  // Distinct procedure names, in the order the tenant listed them, for the
  // per-segment name dropdown.
  const procedureOptions = useMemo(
    () =>
      Array.from(new Set(procedures.map((p) => p.name))).map((name) => ({
        value: name,
        label: name,
      })),
    [procedures],
  );

  const addBoundaryAt = useCallback(
    (t: number) => {
      if (t <= 0 || t >= durationS) return;
      setBoundaries((prev) => {
        if (prev.some((b) => Math.abs(b - t) < 0.05)) return prev;
        return [...prev, t].sort((a, b) => a - b);
      });
      setDropped(new Set());
      setNames({});
    },
    [durationS],
  );

  const removeBoundary = useCallback((t: number) => {
    setBoundaries((prev) => prev.filter((b) => b !== t));
    setDropped(new Set());
    setNames({});
  }, []);

  const toggleDropped = useCallback((index: number) => {
    setDropped((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  }, []);

  // Scrubbing the session-elapsed playhead drives the video (when there
  // is one) to the matching video time.
  const seekTo = useCallback(
    (sessionT: number) => {
      setPlayhead(sessionT);
      const v = videoRef.current;
      if (v && video) {
        v.currentTime = clamp(sessionT - video.offset, 0, v.duration || 1e9);
      }
    },
    [video],
  );

  const handleConfirm = async () => {
    setStage('submitting');
    setError(null);
    try {
      // The split runs in the background (SCALE-SPLIT-TO-WORKER): queue it,
      // then follow it until it is done. Closing the dialog doesn't stop it —
      // the header's "Splitting the recording" indicator keeps showing it.
      const { request_id } = await sessionsApi.splitCaptureSession(
        tenantId,
        event.id,
        buildSplitPayload(segments, dropped, names),
      );
      for (;;) {
        const st = await sessionsApi.getSplitRequest(tenantId, request_id);
        if (st.status === 'done') break;
        if (st.status === 'failed') throw new Error(st.error ?? 'Split failed.');
        if (!openRef.current) return;
        await new Promise((r) => setTimeout(r, SPLIT_POLL_MS));
      }
      onSplitDone();
      onClose();
    } catch (e) {
      setError(
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
          (e as Error).message ??
          'Split failed.',
      );
      setStage('ready');
    }
  };

  const title = `Split session — ${event.procedureName ?? 'Session'}${
    event.participantLabel ? ` · ${event.participantLabel}` : ''
  }`;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {stage === 'loading' && (
          <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
            <CircularProgress />
            {loadedBytes > 0 && (
              <Typography variant="body2" color="text.secondary">
                Reading the recording… {Math.round(loadedBytes / 1e6)} MB
              </Typography>
            )}
          </Stack>
        )}
        {stage === 'error' && (
          <Alert severity="error">{error ?? 'Failed to load.'}</Alert>
        )}
        {(stage === 'ready' || stage === 'submitting') && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {video
                ? 'Watch the recording to find where one session should end. '
                : ''}
              Mark split points on the timeline, then choose which segments
              to keep — each kept segment becomes its own session (re-scored,
              with its video/audio trimmed to that window). The original is
              disabled (restorable).
            </Typography>

            {/* The actual recording — the operator scrubs it to find cuts.
                Its time drives the session-elapsed playhead. */}
            {video && (
              <Box sx={{ position: 'relative', lineHeight: 0 }}>
              <VideoClock
                getVideo={getVideo}
                startedAt={event.capturedAt}
                offsetS={video.offset}
              />
              <video
                ref={videoRef}
                src={video.url}
                controls
                style={{
                  width: '100%',
                  maxHeight: 320,
                  background: '#000',
                  borderRadius: 4,
                }}
                onTimeUpdate={(e) => {
                  const v = e.currentTarget;
                  setPlayhead(
                    clamp(v.currentTime + video.offset, 0, durationS || 1e9),
                  );
                }}
              />
              </Box>
            )}

            {/* EEG focus trace over the full session — click to drop a
                split point at that time. Playhead + boundaries overlaid. */}
            {traits.length > 0 && (
              <Box sx={{ height: 120, cursor: 'crosshair' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={traits}
                    onClick={(s) => {
                      const t = Number(
                        (s as { activeLabel?: number })?.activeLabel,
                      );
                      if (!Number.isNaN(t)) addBoundaryAt(t);
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="t"
                      type="number"
                      domain={[0, durationS]}
                      tickFormatter={(t) => fmt(Number(t))}
                    />
                    <YAxis domain={[0, 100]} width={28} />
                    <Line
                      type="monotone"
                      dataKey="focus"
                      stroke="#1976d2"
                      dot={false}
                      isAnimationActive={false}
                      connectNulls
                    />
                    {boundaries.map((b) => (
                      <ReferenceLine
                        key={b}
                        x={b}
                        stroke="#d32f2f"
                        strokeDasharray="4 3"
                      />
                    ))}
                    <ReferenceLine x={playhead} stroke="#43a047" />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}

            {/* Segment bar — proportional blocks, click to keep/drop. */}
            <Box
              sx={{
                display: 'flex',
                height: 48,
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {segments.map((s) => {
                const isDropped = dropped.has(s.index);
                return (
                  <Box
                    key={s.index}
                    onClick={() => toggleDropped(s.index)}
                    role="button"
                    aria-label={`Segment ${s.index + 1} ${
                      isDropped ? 'dropped' : 'kept'
                    }`}
                    sx={{
                      flexGrow: s.end - s.start,
                      flexBasis: 0,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRight: '1px solid',
                      borderColor: 'background.paper',
                      bgcolor: isDropped
                        ? 'action.disabledBackground'
                        : 'primary.light',
                      color: isDropped ? 'text.disabled' : 'primary.contrastText',
                      fontSize: '0.7rem',
                    }}
                  >
                    {isDropped ? 'drop' : fmt(s.end - s.start)}
                  </Box>
                );
              })}
            </Box>

            {/* Scrubber (only when there's no video to scrub) + add-split. */}
            <Stack direction="row" spacing={2} alignItems="center">
              {!video && (
                <Slider
                  value={playhead}
                  min={0}
                  max={durationS}
                  step={0.1}
                  onChange={(_, v) => seekTo(v as number)}
                  aria-label="Split point position"
                  sx={{ flexGrow: 1 }}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCutIcon />}
                onClick={() => addBoundaryAt(playhead)}
                disabled={playhead <= 0 || playhead >= durationS}
              >
                Add split at {fmt(playhead)}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Total {fmt(durationS)}
              </Typography>
            </Stack>

            {/* Split points, removable. */}
            {boundaries.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                  Split points:
                </Typography>
                {boundaries.map((b) => (
                  <Chip
                    key={b}
                    label={fmt(b)}
                    size="small"
                    onDelete={() => removeBoundary(b)}
                  />
                ))}
              </Stack>
            )}

            {/* Segment keep/drop list. */}
            <Stack spacing={0.5}>
              {segments.map((s) => (
                <Stack
                  key={s.index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <Typography variant="body2" sx={{ minWidth: 160 }}>
                    Segment {s.index + 1} · {fmt(s.start)}–{fmt(s.end)} ·{' '}
                    {fmt(s.end - s.start)}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={dropped.has(s.index) ? 'drop' : 'keep'}
                    onChange={(_, v) => {
                      if (v) toggleDropped(s.index);
                    }}
                  >
                    <ToggleButton
                      value="keep"
                      aria-label={`Keep segment ${s.index + 1}`}
                    >
                      Keep
                    </ToggleButton>
                    <ToggleButton
                      value="drop"
                      aria-label={`Drop segment ${s.index + 1}`}
                    >
                      Drop
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {/* Name the kept segment by picking the procedure it
                      captures — becomes the child session's label on the
                      Activity list. Disabled once dropped. */}
                  <SearchableSelect
                    size="small"
                    label="Procedure"
                    id={`split-segment-${s.index + 1}-procedure`}
                    ariaLabel={`Procedure for segment ${s.index + 1}`}
                    value={names[s.index] ?? ''}
                    disabled={dropped.has(s.index)}
                    onChange={(v) =>
                      setNames((prev) => ({
                        ...prev,
                        [s.index]: v,
                      }))
                    }
                    pinned={NO_PROCEDURE}
                    options={procedureOptions}
                    sx={{ flexGrow: 1, maxWidth: 260 }}
                  />
                </Stack>
              ))}
            </Stack>

            {phases.length > 1 && (
              <Typography variant="caption" color="text.secondary">
                Phases:{' '}
                {phases
                  .map((p) => `${p.phase} (${fmt(p.startS)}–${fmt(p.endS)})`)
                  .join(' · ')}
              </Typography>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Alert severity="info">
              {canConfirmSplit(boundaries, keptCount)
                ? stage === 'submitting'
                  ? 'Splitting — a long recording takes a few minutes. You can close this window: the split carries on, and the new sessions appear when it is done.'
                  : `This creates ${keptCount} session${
                      keptCount === 1 ? '' : 's'
                    } and disables the original (restorable).`
                : 'Add at least one split point and keep at least one segment.'}
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {stage === 'submitting' ? 'Close — keep splitting' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={
            stage !== 'ready' || !canConfirmSplit(boundaries, keptCount)
          }
        >
          {stage === 'submitting'
            ? 'Splitting…'
            : `Split into ${keptCount} session${keptCount === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
